import { BodyPrimary, BodySecondary, H1 } from '@liveauctioneers/hammer-ui-core/text';
import {
    PromotionalBanner,
    PromotionalBannerVariant,
} from '@liveauctioneers/hammer-ui-liveauctioneers/promotionalBanner';

import { useAppSelector } from '@/redux/hooks';
import { getSearchUiState } from '@/redux/modules/search/search.selectors';
import { type CollectionConfig, CollectionLayout } from '@/utils/collections/collections.types';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { useGetFormattedSearchResultsText } from '../../hooks/useGetFormattedSearchResultsText';

type Props = {
    collection: CollectionConfig;
    isArchivedSearchPage: boolean;
};

/**
 * Hero for /collections/<slug>. Two treatments, chosen by `collection.layout`:
 * the LAAC-2371 flat hero, and the pre-existing May-4th Star Wars banner.
 */
export const CollectionBanner = ({ collection, isArchivedSearchPage }: Props) => {
    const { totalFound } = useAppSelector(getSearchUiState);
    const formatMessage = useAppFormatMessage();
    const totalFoundRounded = useGetFormattedSearchResultsText({ totalFound });

    // String() because hammer-ui's PromotionalBanner takes a string `bottomText`, while
    // formatMessage returns ReactNode[] once values are interpolated. `itemNoun` is passed
    // unconditionally -- the price-results string has no slot for it and simply ignores it.
    const bottomText = String(
        formatMessage(
            ['searchPage', 'collectionBanner', isArchivedSearchPage ? 'itemCountPriceResults' : 'itemCountUpcoming'],
            { values: { itemNoun: collection.itemNoun ?? 'items', totalFoundRounded } }
        )
    );

    // The pre-existing May-4th page, preserved exactly. Copy now comes from config rather
    // than src/intl/en.ts; the rendered markup is unchanged.
    if (collection.layout === CollectionLayout.LegacyPromo) {
        return (
            <PromotionalBanner
                bottomText={bottomText}
                description={collection.description}
                title={collection.title}
                variant={PromotionalBannerVariant.STAR_WARS}
            />
        );
    }

    return (
        <div
            className="flex w-full items-center justify-center bg-background-secondary"
            data-testid="collection-banner"
        >
            <div className="flex max-w-[796px] flex-1 flex-col items-center gap-xs px-md pt-lg pb-xl text-center">
                <H1>{collection.title}</H1>
                <BodyPrimary>{collection.description}</BodyPrimary>
                <BodySecondary color="secondary">{bottomText}</BodySecondary>
            </div>
        </div>
    );
};
